/**
 * SearchableSelect - A dropdown select with built-in search/filter.
 *
 * Renders as a text input that opens a filtered dropdown list on focus.
 * Users can type to filter options, then click to select. Deduplicates
 * and sorts options alphabetically by label automatically.
 *
 * @example
 * <SearchableSelect
 *	   id="companyId"
 *	   options={[{ value: '1', label: 'Acme Corp' }, { value: '2', label: 'Beta Inc' }]}
 *	   value={selectedId}
 *	   onChange={setSelectedId}
 *	   placeholder="Select company"
 * />
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DropdownFooterAction } from '../../atoms/DropdownFooterAction';
import type { DropdownFooterActionConfig } from '../../atoms/DropdownFooterAction';

export interface SearchableSelectOption {
	value: string;
	label: string;
}

export interface SearchableSelectProps {
	/** Unique id for the input element */
	id: string;
	/** Array of options — will be auto-deduplicated and sorted alphabetically */
	options: SearchableSelectOption[];
	/** Currently selected value */
	value: string;
	/** Called with the new value when user selects an option */
	onChange: (value: string) => void;
	/** Placeholder text shown when no value is selected */
	placeholder?: string;
	/** Additional CSS classes applied to the wrapper div */
	className?: string;
	/** Whether the input is disabled */
	disabled?: boolean;
	/** Error state — adds red border */
	hasError?: boolean;
	/** Optional call-to-action row pinned under the list (e.g. "+ Quick Add
	 *  User"), shown even when nothing matches. Receives the search text. */
	footerAction?: DropdownFooterActionConfig;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({ id, options, value, onChange, placeholder = 'Search...', className = '', disabled = false, hasError = false, footerAction }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchText, setSearchText] = useState('');
	const wrapperRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	/* Deduplicate by BOTH value (ID) and label (display text), then sort A-Z.
	 * Two records with different IDs but the same label look like dupes to the user. */
	const sortedOptions = useMemo(() => {
		const seenValues = new Set<string>();
		const seenLabels = new Set<string>();
		const unique = options.filter((opt) => {
			if (seenValues.has(opt.value)) return false;
			const normalizedLabel = opt.label.toLowerCase().trim();
			if (seenLabels.has(normalizedLabel)) return false;
			seenValues.add(opt.value);
			seenLabels.add(normalizedLabel);
			return true;
		});
		return unique.sort((a, b) => a.label.localeCompare(b.label));
	}, [options]);

	// Filter options based on search text
	const filteredOptions = useMemo(() => {
		if (!searchText.trim()) return sortedOptions;
		const lower = searchText.toLowerCase();
		return sortedOptions.filter((opt) => opt.label.toLowerCase().includes(lower));
	}, [sortedOptions, searchText]);

	// Get the display label for the currently selected value
	const selectedLabel = useMemo(() => {
		const found = sortedOptions.find((opt) => opt.value === value);
		return found ? found.label : '';
	}, [sortedOptions, value]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setIsOpen(false);
				setSearchText('');
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Handle selecting an option
	const handleSelect = (optValue: string) => {
		onChange(optValue);
		setIsOpen(false);
		setSearchText('');
	};

	// Handle clearing the selection
	const handleClear = () => {
		onChange('');
		setSearchText('');
		inputRef.current?.focus();
	};

	// Handle input focus — open the dropdown and clear search to show all options
	const handleFocus = () => {
		if (!disabled) {
			setIsOpen(true);
			setSearchText('');
		}
	};

	// Border styling based on error/focus state
	const borderClass = hasError
		? 'border-red-500 focus:ring-red-500'
		: 'border-brand-border focus:border-brand-primary focus:ring-brand-primary';

	return (
		<div ref={wrapperRef} className={`searchable-select relative ${className}`} data-ref="molecule-searchable-select">
			{/* Search/display input */}
			<div className="relative">
				<input
					ref={inputRef}
					id={id}
					type="text"
					value={isOpen ? searchText : selectedLabel}
					onChange={(e) => setSearchText(e.target.value)}
					onFocus={handleFocus}
					disabled={disabled}
					placeholder={isOpen ? 'Type to search...' : placeholder}
					className={`w-full px-12px py-8px pr-32px min-h-44px desktop:min-h-36px text-16px font-sans rounded-md border ${borderClass} outline-none focus:ring-2 focus:ring-opacity-20 disabled:opacity-50 disabled:cursor-not-allowed bg-white`}
					autoComplete="off"
				/>
				{/* Clear button (shown when a value is selected and not disabled) */}
				{value && !disabled && (
					<button type="button" onClick={handleClear} className="absolute right-8px top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 min-w-44px min-h-44px flex items-center justify-center" aria-label="Clear selection">
						<svg className="w-16px h-16px" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
					</button>
				)}
				{/* Chevron icon (shown when no value selected) */}
				{!value && (
					<svg className="absolute right-12px top-1/2 -translate-y-1/2 w-16px h-16px text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
				)}
			</div>

			{/* Dropdown options list */}
			{/* Panel = scrolling list + optional footer action pinned below it. */}
			{isOpen && (
				<div className="searchable-select__panel absolute z-50 mt-4px w-full bg-white border border-brand-border rounded-md shadow-lg overflow-hidden">
				<ul className="searchable-select__dropdown max-h-224px overflow-y-auto" role="listbox">
					{filteredOptions.length === 0 ? (
						<li className="px-12px py-8px text-14px text-brand-text-muted font-sans">No results found</li>
					) : (
						filteredOptions.map((opt) => (
							<li key={opt.value} role="option" aria-selected={opt.value === value} onClick={() => handleSelect(opt.value)} className={`searchable-select__option px-12px py-8px text-14px font-sans cursor-pointer min-h-44px flex items-center transition-colors ${opt.value === value ? 'searchable-select__option--selected bg-brand-primary text-white' : 'text-brand-text hover:bg-brand-surface'}`}>
								{opt.label}
							</li>
						))
					)}
				</ul>
				{footerAction && (
					<DropdownFooterAction
						label={footerAction.label}
						onSelect={() => {
							const text = searchText;
							setIsOpen(false);
							setSearchText('');
							inputRef.current?.blur();
							footerAction.onSelect(text);
						}}
					/>
				)}
				</div>
			)}
		</div>
	);
};

export default SearchableSelect;
